import { arenaSchema, type Arena } from '@growup/arena-shared';
import { Button, Card, Field, Input, Notice, Table } from '@growup/ui';
import { useState } from 'react';

import { messageOf, send } from '../api';

/** "Arena Porto" -> "arena-porto", the identifier the API accepts. */
export function slugify(name: string): string {
  return name
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40);
}

/** Every arena, and creating a new one. Administrators only. */
export function ArenasView({
  arenas,
  onConfigure,
  onCreated,
}: {
  arenas: Arena[];
  onConfigure: (arenaId: string) => void;
  onCreated: (arena: Arena) => void;
}) {
  const [name, setName] = useState('');
  // Follows the name until someone types an identifier of their own.
  const [slug, setSlug] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const shownSlug = slug ?? slugify(name);

  const createArena = async () => {
    setError(null);
    try {
      const created = arenaSchema.parse(
        await send('POST', '/api/arena/arenas', { name: name.trim(), slug: shownSlug.trim() }),
      );
      setName('');
      setSlug(null);
      onCreated(created);
    } catch (e) {
      setError(messageOf(e));
    }
  };

  return (
    <div className="flex flex-col gap-4">
      <Card headingLevel={2} title="Arenas">
        <Table
          caption="Arenas"
          rowKey={(a) => a.id}
          rows={arenas}
          empty="Ainda não há arenas."
          columns={[
            { key: 'name', header: 'Nome', cell: (a) => a.name },
            {
              key: 'slug',
              header: 'Identificador',
              cell: (a) => <code className="text-caption text-gu-ink-muted">{a.slug}</code>,
            },
            {
              key: 'configure',
              header: <span className="sr-only">Configurar</span>,
              align: 'right' as const,
              cell: (a) => (
                <Button
                  variant="secondary"
                  size="sm"
                  aria-label={`Configurar ${a.name}`}
                  onClick={() => onConfigure(a.id)}
                >
                  Configurar
                </Button>
              ),
            },
          ]}
        />
      </Card>

      <Card headingLevel={2} title="Nova arena">
        <form
          className="flex flex-col gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            void createArena();
          }}
        >
          <div className="grid grid-cols-1 items-end gap-2 sm:grid-cols-[1fr_1fr_auto]">
            <Field label="Nome">
              <Input value={name} onChange={(e) => setName(e.target.value)} />
            </Field>
            <Field label="Identificador">
              <Input
                value={shownSlug}
                aria-describedby="arena-slug-hint"
                onChange={(e) => setSlug(e.target.value)}
              />
            </Field>
            <Button type="submit" disabled={name.trim() === '' || shownSlug.trim() === ''}>
              Criar arena
            </Button>
          </div>
          {/* Fica por baixo da linha toda, para nao empurrar so uma das caixas. */}
          <p id="arena-slug-hint" className="text-caption text-gu-ink-muted">
            O identificador só leva minúsculas, algarismos e hífens. Preenche-se a partir do nome.
          </p>
          {error ? <Notice tone="danger">{error}</Notice> : null}
        </form>
      </Card>
    </div>
  );
}
